import React, { useEffect, useMemo, useState, useCallback } from 'react'
import { connect } from 'react-redux'
import Api from 'domain/api'
import { history } from 'lib/history'
import * as storage from 'lib/storage'
import ModalDialog from 'components/ModalDialog'
import { signOut as signOutAction } from 'domain/env'

const POST_CONSENT_REDIRECT_KEY = 'post_consent_redirect_URL'

interface ConsentDoc {
  id: number
  type: string
  version?: string
  contentPath?: string
}

interface Props {
  signOut: (payload?: any) => void
}

function Consent({ signOut }: Props) {
  const [loading, setLoading] = useState(true)
  const [missing, setMissing] = useState<ConsentDoc[]>([])
  const [error, setError] = useState<string | null>(null)
  const [accepting, setAccepting] = useState(false)

  const token = useMemo(() => {
    try {
      return JSON.parse(storage.getStorageItem(storage.TOKENS_KEY, '')) as string
    } catch {
      return null
    }
  }, [])

  const handleSignOut = useCallback(() => {
    signOut({})
    history.replace('/')
  }, [signOut])

  async function loadStatus() {
    if (!token) {
      history.replace('/')
      return
    }

    setLoading(true)
    setError(null)

    try {
      const res = await Api.consentMe({
        headers: { Authorization: `Bearer ${token}` }
      })

      const data = res.data && res.data.data
      const consentRequired = Boolean(data && data.consentRequired)

      if (!consentRequired) {
        const redirect = window.localStorage.getItem(POST_CONSENT_REDIRECT_KEY) || '/dashboard'
        try {
          window.localStorage.removeItem(POST_CONSENT_REDIRECT_KEY)
        } catch {
          // ignore
        }
        history.replace(redirect)
        return
      }

      setMissing((data && data.missing) || [])
    } catch (e) {
      setError('Unable to load consent status')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadStatus()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const acceptAll = async () => {
    if (!token) return

    setAccepting(true)
    setError(null)

    try {
      for (const doc of missing) {
        await Api.acceptConsent({
          headers: { Authorization: `Bearer ${token}` },
          data: {
            documentId: doc.id,
            channel: 'web',
            metadata: null
          }
        })
      }

      await loadStatus()
    } catch (e) {
      setError('Failed to accept consent. Please try again.')
    } finally {
      setAccepting(false)
    }
  }

  return (
    <ModalDialog
      open={true}
      onClose={handleSignOut}
      title="Terms & Privacy Consent"
      width={560}
      cancelText="Sign out"
      buttenText={accepting ? 'Accepting…' : 'Accept'}
      onApprove={acceptAll}
      approveDisabled={loading || accepting || missing.length === 0}
      closeOnOverlayClick={false}
    >
      <p style={{ marginTop: 0 }}>You must review and accept the latest documents to continue.</p>

      {error && <div style={{ marginBottom: 12, color: 'red' }}>{error}</div>}

      {loading && <div>Loading consent…</div>}

      {!loading && missing.length > 0 && (
        <ul style={{ margin: 0, paddingLeft: 18 }}>
          {missing.map(doc => {
            const label = `${doc.type}${doc.version ? ` (v${doc.version})` : ''}`

            return (
              <li key={doc.id} style={{ marginBottom: 8 }}>
                {doc.contentPath ? (
                  <a
                    href={doc.contentPath}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={e => e.stopPropagation()}
                  >
                    {label}
                  </a>
                ) : (
                  <span>{label}</span>
                )}
              </li>
            )
          })}
        </ul>
      )}

      {!loading && missing.length === 0 && <div>No pending documents.</div>}
    </ModalDialog>
  )
}

export default connect(
  null,
  { signOut: signOutAction }
)(Consent)
